import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  BIENS_COLUMNS,
  DEFAULT_VISIBLE_COLUMNS,
  loadVisibleColumns as loadFromLocal,
  saveVisibleColumns as saveToLocal,
  type BiensColumnKey,
} from "@/lib/biensColumns";

/**
 * Préférence "colonnes affichées dans /biens" :
 *   - Authentifié : profile.prefs_columns_biens (Supabase, par utilisateur)
 *   - Test mode / non authentifié : localStorage (par navigateur)
 *
 * Le save met à jour Supabase puis patch le profile local pour éviter un
 * re-fetch. Échec réseau → log seulement, l'UI conserve la valeur optimiste.
 *
 * Cf. migration 043_add_profiles_prefs_columns_biens.sql.
 */
export function useColumnPreferences(): {
  columns: BiensColumnKey[];
  save: (next: BiensColumnKey[]) => Promise<void>;
} {
  const { profile, isDevSession, patchProfile } = useAuth();

  // Source = profile si dispo, sinon localStorage. Recalcul à chaque changement
  // de profile (ex: login → prefs cross-device récupérées).
  const sourced = useMemo<BiensColumnKey[]>(() => {
    if (profile && Array.isArray(profile.prefs_columns_biens)) {
      return sanitize(profile.prefs_columns_biens);
    }
    if (profile) {
      // Authentifié sans prefs → défauts (NULL dans la DB)
      return DEFAULT_VISIBLE_COLUMNS;
    }
    // Pas de profile (test mode, ou en attente) → localStorage
    return loadFromLocal();
  }, [profile]);

  // Optimistic local copy : permet d'afficher la nouvelle sélection avant
  // que Supabase confirme. Resync si la source change.
  const [columns, setColumns] = useState<BiensColumnKey[]>(sourced);
  useEffect(() => {
    setColumns(sourced);
  }, [sourced]);

  const save = useCallback(
    async (next: BiensColumnKey[]) => {
      const cleaned = sanitize(next);
      setColumns(cleaned); // optimistic

      // Test mode / pas authentifié → localStorage seulement
      if (!profile || isDevSession) {
        saveToLocal(cleaned);
        return;
      }

      // Authentifié → Supabase. patchProfile met aussi à jour la copie locale
      // de l'AuthContext pour que les autres consumers re-rendent.
      const { error } = await supabase
        .from("profiles")
        .update({ prefs_columns_biens: cleaned })
        .eq("id", profile.id);

      if (error) {
        console.error("[useColumnPreferences] save failed:", error.message);
        return;
      }

      patchProfile({ prefs_columns_biens: cleaned });
      // Mirror dans localStorage : permet à BiensPrintPage (nouvel onglet) de
      // lire la sélection AVANT que le profile soit re-fetché.
      saveToLocal(cleaned);
    },
    [profile, isDevSession, patchProfile],
  );

  return { columns, save };
}

/** Filtre les clés inconnues (forward-compat si on retire une colonne). */
function sanitize(keys: unknown[]): BiensColumnKey[] {
  const known = new Set(BIENS_COLUMNS.map((c) => c.key));
  const valid = keys.filter(
    (k): k is BiensColumnKey => typeof k === "string" && known.has(k as BiensColumnKey),
  );
  return valid.length > 0 ? valid : DEFAULT_VISIBLE_COLUMNS;
}
